import type { SessionContext, SessionPermission } from '@mervonn/shared-types';

/**
 * Read model over the session's effective permissions (from `GET /auth/me`).
 * The API is always the real authority — a hidden button the user forces
 * still hits a server-side `@RequirePermission` — but the UI mirrors the
 * RBAC matrix so it never *offers* an action the caller can't take
 * (UX Specification §3 / §10).
 */
export class Permissions {
  private readonly byCode: Map<string, SessionPermission>;

  constructor(private readonly session: SessionContext) {
    this.byCode = new Map(session.permissions.map((p) => [p.code, p]));
  }

  /** Does the caller hold this permission at any scope? */
  can(code: string): boolean {
    return this.byCode.has(code);
  }

  /** Hold any one of these? */
  canAny(codes: string[]): boolean {
    return codes.some((c) => this.byCode.has(c));
  }

  scopeOf(code: string): SessionPermission['scope_type'] | undefined {
    return this.byCode.get(code)?.scope_type;
  }

  get roleCode() {
    return this.session.role_code;
  }

  get isPlatform(): boolean {
    return this.session.tenant_id === null;
  }
}
